import React, { ReactNode, Ref } from 'react';
import MuiIconButton, { IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import { styled, Theme } from '@mui/material/styles';

import getColors from 'utils/getColors';
import getShadow from 'utils/getShadow';
import { withAlpha } from 'utils/colorUtils';

type IconButtonVariant = 'text' | 'contained' | 'outlined' | 'dashed' | 'light' | 'shadow';
type IconButtonShape = 'square' | 'rounded';
type IconButtonColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

function getColorStyle({ variant, theme, color }: { variant?: IconButtonVariant; theme: Theme; color?: string }) {
  const colors = getColors(theme, color);
  const { lighter, light, dark, darker, main, contrastText } = colors;

  const buttonShadow = `${color}Button`;
  const shadows = getShadow(theme, buttonShadow);

  // Cor por esquema: no escuro os papéis se invertem (o `main` dos ramps escuros é o tom fundo e
  // o `dark`/`darker` são os claros), senão o ícone cai para ~2:1 sobre o fundo escuro.
  const repouso = `light-dark(${main}, ${dark})`;
  const enfase = `light-dark(${dark}, ${darker})`;
  // Hover do botão preenchido: escurece — no claro pelo token `dark`, no escuro sobre o `main`.
  const filledHover = { backgroundColor: `light-dark(${dark}, color-mix(in srgb, ${main} 78%, #000))` };

  const commonShadow = {
    '&::after': {
      boxShadow: `0 0 6px 6px ${withAlpha(main, 0.9)}`
    },
    '&:active::after': {
      boxShadow: `0 0 0 0 ${withAlpha(main, 0.9)}`
    },
    '&:focus-visible': {
      outline: `2px solid ${dark}`,
      outlineOffset: 2
    }
  };

  switch (variant) {
    case 'contained':
      return { color: contrastText, background: main, '&:hover': filledHover, ...commonShadow };
    case 'light':
      return { color: repouso, background: lighter, '&:hover': { background: withAlpha(light!, 0.5), color: enfase }, ...commonShadow };
    case 'shadow':
      return {
        boxShadow: shadows,
        color: contrastText,
        background: main,
        '&:hover': { boxShadow: 'none', ...filledHover },
        ...commonShadow
      };
    case 'outlined':
      return {
        color: repouso,
        '&:hover': { background: 'transparent', color: enfase, borderColor: enfase },
        ...commonShadow
      };
    case 'dashed':
      return {
        color: repouso,
        background: lighter,
        '&:hover': { color: enfase, borderColor: enfase },
        ...commonShadow
      };
    case 'text':
    default:
      return {
        color: repouso,
        '&:hover': {
          color: enfase,
          background: color === 'secondary' ? withAlpha(light!, 0.1) : lighter
        },
        ...commonShadow
      };
  }
}

interface IconButtonStyleProps {
  variant?: IconButtonVariant;
  shape?: IconButtonShape;
}

const IconButtonStyle = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'shape'
})<IconButtonStyleProps & { color?: string }>(({ theme, color, variant }) => ({
  position: 'relative',
  '::after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    borderRadius: 4,
    opacity: 0,
    transition: 'all 0.5s'
  },
  ':active::after': {
    position: 'absolute',
    borderRadius: 4,
    left: 0,
    top: 0,
    opacity: 1,
    transition: '0s'
  },
  ...getColorStyle({ variant, theme, color }),
  variants: [
    {
      props: { shape: 'rounded' },
      style: {
        borderRadius: '50%',
        '::after': { borderRadius: '50%' },
        ':active::after': { borderRadius: '50%' }
      }
    },
    {
      props: { variant: 'outlined' },
      style: { border: '1px solid', borderColor: 'inherit' }
    },
    {
      props: { variant: 'dashed' },
      style: { border: '1px dashed', borderColor: 'inherit' }
    },
    {
      props: ({ variant }: IconButtonStyleProps) => variant !== 'text',
      style: {
        '&.Mui-disabled': {
          background: theme.vars!.palette.grey[200],
          '&:hover': {
            background: theme.vars!.palette.grey[200],
            color: theme.vars!.palette.grey[300],
            borderColor: 'inherit'
          }
        }
      }
    }
  ]
}));

interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  variant?: IconButtonVariant;
  shape?: IconButtonShape;
  children?: ReactNode;
  color?: IconButtonColor;
  ref?: Ref<HTMLButtonElement>;
}

function IconButton({ variant = 'text', shape = 'square', children, color = 'primary', ref, ...others }: IconButtonProps) {
  const Styled = IconButtonStyle as unknown as React.ComponentType<
    Omit<MuiIconButtonProps, 'color'> & IconButtonStyleProps & { color?: string }
  >;
  return (
    <Styled ref={ref} disableRipple variant={variant} shape={shape} color={color} {...(others as Omit<MuiIconButtonProps, 'color'>)}>
      {children}
    </Styled>
  );
}

IconButton.displayName = 'IconButton';

export default IconButton;
